/**
 * m3 echo Frontend - Zitat-Anteil (Analyse-Tab)
 *
 * Zeigt Zitat-Anteil, Websuch-Abdeckung und Auflösungsquote immer gemeinsam,
 * jeweils mit absoluten Zahlen. "Unbekannt" wird nie als negativer Befund
 * dargestellt. Das Panel wird nur gerendert, wenn der Mandant das Modul
 * "citation_share" gebucht hat; ohne Modul entstehen keine /citations-Requests.
 *
 * Hinweisschwellen (nur Darstellung, ändern keine Berechnung):
 * Websuch-Abdeckung < 80 %, Auflösungsquote < 90 %, weniger als 20 Antworten
 * mit Websuche, unbekannter Suchstatus, fehlende Zitatzuordnung, Wechsel von
 * Modell oder Domainkonfiguration.
 */

import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip,
  CircularProgress, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  useCitationAutopilotOptions, useCitationDomains, useCitationModuleActive, useProjectCitations,
} from '../hooks/useCitations';
import { useIsAdmin } from '../stores/useAuthStore';
import { useUIStore } from '../stores/useUIStore';
import { citationWindow } from '../utils/citationWindow';
import { apiErrorStatus } from '../utils/apiErrorStatus';
import CitationDomainsDialog from './CitationDomainsDialog';
import type { CitationFilters, CitationMetric, ProjectCitations } from '../types/api';

const LOW_COVERAGE_THRESHOLD = 0.8;
const LOW_RESOLUTION_THRESHOLD = 0.9;
const MIN_SEARCH_ANSWERS = 20;

const DASH = '—';

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  const rounded = Math.round(value * 1000) / 10;
  return `${String(rounded).replace('.', ',')} %`;
};

const WARNING_LABELS: Record<string, string> = {
  search_status_unknown: 'Bei einem Teil der Antworten ist die Suchausführung nicht erfasst.',
  citation_status_unknown: 'Bei einem Teil der Suchantworten ist die Zitatzuordnung unbekannt.',
  sources_pending: 'Ein Teil der Quellen wird noch aufgelöst.',
};

const SOURCE_OPTIONS: [string, string][] = [
  ['', 'Alle Herkünfte'],
  ['manual', 'Manuell'],
  ['retest', 'Wiederholung'],
  ['autopilot', 'Autopilot'],
  ['custom_autopilot', 'Eigener Autopilot'],
  ['optimization', 'Optimierung'],
  ['unknown', 'Herkunft unbekannt'],
];

const PERIOD_OPTIONS = [7, 30, 90, 366];

const OWNERSHIP_LABELS: Record<string, string> = {
  own: 'eigene Domain',
  external: 'fremde Domain',
  mixed: 'gemischt',
  unknown: 'Zuordnung unbekannt',
};

interface MetricProps {
  testId: string;
  title: string;
  /** Title of the compact card on phones. */
  shortTitle: string;
  value: string;
  /** Absolute numbers ("6/12") shown on the compact card; omitted without a value. */
  ratio?: string;
  description: React.ReactNode;
  note?: React.ReactNode;
  incomplete?: boolean;
}

const MetricChip: React.FC = () => (
  <Chip size="small" variant="outlined" label="bisher nachgewiesen" sx={{ mt: 0.5, maxWidth: '100%' }} />
);

const MetricCard: React.FC<MetricProps & { compact: boolean }> = (
  { testId, title, shortTitle, value, ratio, description, note, incomplete, compact },
) => (
  <Card variant="outlined" data-testid={testId} sx={{ minWidth: 0 }}>
    <CardContent sx={{ p: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
      <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
        {compact ? shortTitle : title}
      </Typography>
      <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 700 }}>{value}</Typography>
      {compact ? (
        ratio && <Typography variant="caption" color="text.secondary">{ratio}</Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
            {description}
          </Typography>
          {note && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
              {note}
            </Typography>
          )}
          {incomplete && <MetricChip />}
        </>
      )}
    </CardContent>
  </Card>
);

/** Explanations of the compact cards, below the row so all three values stay side by side. */
const MetricDetails: React.FC<{ metrics: MetricProps[] }> = ({ metrics }) => (
  <Box data-testid="citation-metric-details" sx={{ mb: 1.5 }}>
    {metrics.map((m) => (
      <Box key={m.testId} sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          <Box component="span" sx={{ fontWeight: 600 }}>{m.title}:</Box> {m.description}
        </Typography>
        {m.note && (
          <Typography variant="caption" color="text.secondary" display="block">{m.note}</Typography>
        )}
        {m.incomplete && <MetricChip />}
      </Box>
    ))}
  </Box>
);

const metricIncomplete = (m: CitationMetric) => m.incomplete && m.value !== null;

interface PanelProps {
  projectId: number;
  canEdit: boolean;
  models?: { model_code: string; display_name: string }[];
  prompts?: { id: number; label: string }[];
  /** Mandant des Projekts; ermöglicht Plattform-Admins die Auswahl eigener Autopilots. */
  tenantId?: number;
}

const CitationSharePanelContent: React.FC<PanelProps> = ({ projectId, canEdit, models = [], prompts = [], tenantId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { openJobDetailDialog } = useUIStore();

  const [days, setDays] = useState(30);
  const [model, setModel] = useState('');
  const [promptId, setPromptId] = useState('');
  const [source, setSource] = useState('');
  const [autopilot, setAutopilot] = useState('');
  const [groupBy, setGroupBy] = useState<'week' | 'job'>('week');
  const [companyDraft, setCompanyDraft] = useState('');
  const [company, setCompany] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const isAdmin = useIsAdmin();
  const autopilotQuery = useCitationAutopilotOptions(projectId, tenantId, isAdmin);
  const autopilotOptions = autopilotQuery.data ?? [];

  const period = useMemo(() => citationWindow(days), [days]);
  const filters: CitationFilters = useMemo(() => {
    const f: CitationFilters = { from: period.from, to: period.to, group_by: groupBy };
    if (model) f.model = model;
    if (promptId) f.prompt_id = Number(promptId);
    if (autopilot.startsWith('std:')) f.autopilot_id = Number(autopilot.slice(4));
    else if (autopilot.startsWith('custom:')) f.custom_autopilot_id = Number(autopilot.slice(7));
    else if (source) f.source = source;
    if (company.trim()) f.first_mentioned_company = company.trim();
    return f;
  }, [period, groupBy, model, promptId, source, autopilot, company]);

  const query = useProjectCitations(projectId, filters);
  const domainsQuery = useCitationDomains(projectId);

  if (query.isError && apiErrorStatus(query.error) === 403) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Das Modul Zitat-Anteil ist für dieses Projekt nicht aktiviert.
      </Alert>
    );
  }

  const data: ProjectCitations | undefined = query.data;

  const commitCompany = () => setCompany(companyDraft);

  const filterRow = (
    <Box
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}
      role="group"
      aria-label="Filter Zitat-Anteil"
    >
      <TextField select size="small" label="Zeitraum" value={days}
        onChange={(e) => setDays(Number(e.target.value))}
        SelectProps={{ native: true }} sx={{ minWidth: 130, flex: isMobile ? '1 1 45%' : undefined }}>
        {PERIOD_OPTIONS.map((d) => <option key={d} value={d}>{`Letzte ${d} Tage`}</option>)}
      </TextField>
      <TextField select size="small" label="Modell" value={model}
        onChange={(e) => setModel(e.target.value)}
        SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150, flex: isMobile ? '1 1 45%' : undefined }}>
        <option value="">Alle Modelle</option>
        {models.map((m) => <option key={m.model_code} value={m.model_code}>{m.display_name}</option>)}
      </TextField>
      {prompts.length > 0 && (
        <TextField select size="small" label="Prompt" value={promptId}
          onChange={(e) => setPromptId(e.target.value)}
          SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150, maxWidth: '100%', flex: isMobile ? '1 1 45%' : undefined }}>
          <option value="">Alle Prompts</option>
          {prompts.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </TextField>
      )}
      <TextField select size="small" label="Herkunft" value={autopilot ? '' : source}
        onChange={(e) => setSource(e.target.value)} disabled={!!autopilot}
        SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 150, flex: isMobile ? '1 1 45%' : undefined }}>
        {SOURCE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </TextField>
      {autopilotOptions.length > 0 && (
        <TextField select size="small" label="Autopilot" value={autopilot}
          onChange={(e) => setAutopilot(e.target.value)}
          SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, maxWidth: '100%', flex: isMobile ? '1 1 45%' : undefined }}>
          <option value="">Alle Autopilots</option>
          {autopilotOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </TextField>
      )}
      <TextField select size="small" label="Verlauf je" value={groupBy}
        onChange={(e) => setGroupBy(e.target.value as 'week' | 'job')}
        SelectProps={{ native: true }} sx={{ minWidth: 110, flex: isMobile ? '1 1 45%' : undefined }}>
        <option value="week">Woche</option>
        <option value="job">Test</option>
      </TextField>
      <TextField size="small" label="Zuerst genannte Firma" value={companyDraft}
        onChange={(e) => setCompanyDraft(e.target.value)}
        onBlur={commitCompany}
        onKeyDown={(e) => { if (e.key === 'Enter') commitCompany(); }}
        helperText="Marken und Produkte sind technisch eingeschlossen."
        sx={{ minWidth: 200, flex: isMobile ? '1 1 100%' : undefined }} />
    </Box>
  );

  const header = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
      <Typography variant="h6">
        Zitat-Anteil · Websuch-Abdeckung · Auflösungsquote
      </Typography>
      {canEdit && (
        <Button size="small" variant="outlined" onClick={() => setDialogOpen(true)} data-testid="citation-domains-edit">
          {domainsQuery.data?.config === null ? 'Eigene Domain bestätigen' : 'Eigene Domains bearbeiten'}
        </Button>
      )}
    </Box>
  );

  let body: React.ReactNode;
  if (query.isPending) {
    body = (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} aria-label="Zitat-Anteil wird geladen" />
      </Box>
    );
  } else if (query.isError || !data) {
    body = (
      <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => query.refetch()}>Wiederholen</Button>}>
        Der Zitat-Anteil konnte nicht geladen werden.
      </Alert>
    );
  } else {
    body = <CitationResults
      data={data}
      noDomain={domainsQuery.data?.config === null}
      domainConfirmed={!!domainsQuery.data?.config}
      canEdit={canEdit}
      isMobile={isMobile}
      onConfirmDomain={() => setDialogOpen(true)}
      onOpenJob={openJobDetailDialog}
    />;
  }

  return (
    <Card sx={{ mb: 3 }} data-testid="citation-share-panel">
      <CardContent>
        {header}
        {filterRow}
        {body}
      </CardContent>
      {dialogOpen && (
        <CitationDomainsDialog open={dialogOpen} projectId={projectId} onClose={() => setDialogOpen(false)} />
      )}
    </Card>
  );
};

interface ResultsProps {
  data: ProjectCitations;
  noDomain: boolean;
  /** Projekt hat eine Domainkonfiguration; sie gilt erst für Tests, die danach starten. */
  domainConfirmed: boolean;
  canEdit: boolean;
  isMobile: boolean;
  onConfirmDomain: () => void;
  onOpenJob: (jobId: number) => void;
}

const CitationResults: React.FC<ResultsProps> = ({ data, noDomain: noDomainFromConfig, domainConfirmed, canEdit, isMobile, onConfirmDomain, onOpenJob }) => {
  const { answers, metrics, sources, quality, domain_basis: basis } = data;

  if (answers.total === 0) {
    return <Typography color="text.secondary">Für diese Auswahl liegen noch keine Antworten vor.</Typography>;
  }

  const noDomain =
    noDomainFromConfig || (answers.web_search > 0 && basis.search_answers_without_config >= answers.web_search);
  const noSearch = answers.web_search === 0;
  const onlyLegacy = noSearch && answers.legacy === answers.total;
  const noSources = !noSearch && sources.total === 0;

  // Zitat-Anteil
  let shareValue = DASH;
  let shareDescription: React.ReactNode;
  if (noDomain && domainConfirmed) {
    shareDescription = 'Die eigene Domain ist bestätigt und zählt ab dem nächsten Test. Die Antworten in diesem Zeitraum liefen noch ohne.';
  } else if (noDomain) {
    shareDescription = (
      <>
        Eigene Domain bestätigen, um den Zitat-Anteil zu messen.
        {canEdit && (
          <>
            {' '}
            <Button size="small" onClick={onConfirmDomain}>Eigene Domain bestätigen…</Button>
          </>
        )}
      </>
    );
  } else if (noSearch) {
    shareDescription = onlyLegacy
      ? 'Für diese Antworten wurde die Suchausführung noch nicht erfasst.'
      : 'Keine Antworten mit nachgewiesener Websuche.';
  } else {
    shareValue = formatPercent(metrics.citation_share.value);
    shareDescription = `${metrics.citation_share.numerator} von ${metrics.citation_share.denominator} Antworten mit Websuche zitieren mindestens eine eigene Domain.`;
  }
  const shareIncomplete = !noDomain && !noSearch && metricIncomplete(metrics.citation_share);

  // Websuch-Abdeckung
  const cov = metrics.web_search_coverage;
  // Kein einziger bekannter Suchstatus: Abdeckung unbekannt, nicht 0 %.
  const coverageUnknown = cov.value === null && answers.search_unknown > 0;
  const coverageNote = answers.search_unknown > 0 && !coverageUnknown
    ? `Bei ${answers.search_unknown} Antworten ist die Suchausführung nicht erfasst; Wert = nachgewiesene Abdeckung.`
    : undefined;

  // Auflösungsquote
  const res = metrics.source_resolution_rate;
  const resolutionValue = noSources || noSearch ? DASH : formatPercent(res.value);
  const resolutionDescription = noSearch
    ? DASH
    : noSources
      ? 'Keine Quellen übermittelt'
      : `Für ${res.numerator} von ${res.denominator} Quellen konnte die Zieldomain bestimmt werden.`;

  // Hinweise bei geringer Abdeckung
  const hints: string[] = [];
  if (cov.value !== null && cov.value < LOW_COVERAGE_THRESHOLD) hints.push('Websuch-Abdeckung unter 80 %.');
  if (!noSources && res.value !== null && res.value < LOW_RESOLUTION_THRESHOLD) hints.push('Auflösungsquote unter 90 %.');
  if (answers.web_search < MIN_SEARCH_ANSWERS && !coverageUnknown) hints.push(`Weniger als ${MIN_SEARCH_ANSWERS} Antworten mit Websuche.`);
  if (coverageUnknown) {
    hints.push(onlyLegacy
      ? 'Diese Antworten stammen aus der Zeit vor der Erfassung des Suchstatus.'
      : 'Der Suchstatus ist bei allen Antworten unbekannt.');
  } else if (answers.search_unknown > 0) {
    hints.push('Der Suchstatus ist bei einem Teil der Antworten unbekannt.');
  }
  if (sources.citation_evidence_unknown > 0) hints.push('Bei einem Teil der Quellen fehlt die Zitatzuordnung.');
  const modelSets = new Set(data.trend.map((p) => [...p.models].sort().join('|')));
  if (modelSets.size > 1) hints.push('Die Modellzusammensetzung wechselt im Zeitraum.');
  if (basis.mixed || basis.config_revisions.length > 1) hints.push('Die Domainkonfiguration wechselt im Zeitraum.');

  const metricCards: MetricProps[] = [
    {
      testId: 'metric-citation-share', title: 'Zitat-Anteil', shortTitle: 'Zitat-Anteil', value: shareValue,
      ratio: shareValue === DASH ? undefined : `${metrics.citation_share.numerator}/${metrics.citation_share.denominator}`,
      description: shareDescription, incomplete: shareIncomplete,
      note: answers.own_unknown > 0 && !noDomain && !noSearch
        ? `Bei ${answers.own_unknown} Suchantworten ist noch nicht sicher feststellbar, ob eine eigene Domain zitiert wurde.`
        : undefined,
    },
    {
      testId: 'metric-web-search-coverage', title: 'Websuch-Abdeckung', shortTitle: 'Websuche',
      value: formatPercent(cov.value), ratio: coverageUnknown ? undefined : `${cov.numerator}/${cov.denominator}`,
      description: coverageUnknown
        ? `Für diese ${answers.total} Antworten wurde die Suchausführung noch nicht erfasst.`
        : `${cov.numerator} von ${cov.denominator} Antworten wurden nachweislich mit Websuche erstellt.`,
      note: coverageNote, incomplete: !coverageUnknown && metricIncomplete(cov) && answers.search_unknown > 0,
    },
    {
      testId: 'metric-source-resolution', title: 'Auflösungsquote', shortTitle: 'Auflösung', value: resolutionValue,
      ratio: resolutionValue === DASH ? undefined : `${res.numerator}/${res.denominator}`,
      description: resolutionDescription,
      note: quality.resolution_in_progress ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={10} /> Quellen werden noch aufgelöst
        </Box>
      ) : undefined,
      incomplete: !noSources && !noSearch && metricIncomplete(res) && res.denominator > res.numerator,
    },
  ];

  return (
    <Box>
      <Box
        data-testid="citation-metrics"
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 0.75, sm: 2 }, mb: 1.5 }}
      >
        {metricCards.map((m) => <MetricCard key={m.testId} {...m} compact={isMobile} />)}
      </Box>
      {isMobile && <MetricDetails metrics={metricCards} />}

      {answers.search_fallback > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {`${answers.search_fallback} Antworten wurden nach einem Suchfehler ohne Websuche erstellt.`}
        </Typography>
      )}

      {hints.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="citation-hints">
          Geringe oder unvollständige Datenbasis, keine Bewertung als besser oder schlechter möglich:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {hints.map((h) => <li key={h}>{h}</li>)}
          </Box>
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ mt: 2 }} gutterBottom>Verlauf</Typography>
      <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
        <Table size="small" aria-label="Verlauf Zitat-Anteil">
          <TableHead>
            <TableRow>
              <TableCell>{data.filters.group_by === 'job' ? 'Test' : 'Woche ab'}</TableCell>
              <TableCell align="right">Antworten</TableCell>
              <TableCell align="right">Zitat-Anteil</TableCell>
              <TableCell align="right">Websuch-Abdeckung</TableCell>
              <TableCell align="right">Auflösungsquote</TableCell>
              {!isMobile && <TableCell>Zusammensetzung</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.trend.map((p) => {
              const configChanged = p.mixed_config || p.config_revisions.length > 1;
              return (
                <TableRow key={`${p.bucket_start}-${p.job_id ?? ''}`}>
                  <TableCell>
                    {p.job_id != null
                      ? `Test #${p.job_id} · ${new Date(p.bucket_start).toLocaleDateString('de-DE')}`
                      : new Date(p.bucket_start).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell align="right">{p.answers}</TableCell>
                  <TableCell align="right">{formatPercent(p.citation_share)}</TableCell>
                  <TableCell align="right">{formatPercent(p.web_search_coverage)}</TableCell>
                  <TableCell align="right">{formatPercent(p.source_resolution_rate)}</TableCell>
                  {!isMobile && (
                    <TableCell>
                      {p.models.join(', ') || DASH}
                      {configChanged && ' · Domainkonfiguration gewechselt'}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {data.trend.length === 0 && (
              <TableRow><TableCell colSpan={isMobile ? 5 : 6}>Kein Verlauf für diese Auswahl.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" gutterBottom>Zitierte Domains nach Antworten</Typography>
      {data.top_domains.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Keine zitierten Domains in dieser Auswahl.
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
          <Table size="small" aria-label="Zitierte Domains nach Antworten">
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell align="right">Antworten</TableCell>
                <TableCell align="right">Anteil an Suchantworten</TableCell>
                <TableCell>Zuordnung</TableCell>
                <TableCell>Beleg</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.top_domains.map((d) => (
                <TableRow key={d.domain}>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{d.domain}</TableCell>
                  <TableCell align="right">{d.cited_answer_count}</TableCell>
                  <TableCell align="right">{formatPercent(d.share_of_search_answers)}</TableCell>
                  <TableCell>{OWNERSHIP_LABELS[d.ownership] ?? d.ownership}</TableCell>
                  <TableCell>
                    {d.evidence.length > 0 ? (
                      <Link component="button" variant="body2" onClick={() => onOpenJob(d.evidence[0].job_id)}>
                        Test #{d.evidence[0].job_id}
                      </Link>
                    ) : DASH}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Datenqualität im Detail</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }} data-testid="citation-quality-details">
            <li>{`Antworten gesamt: ${answers.total} (mit Websuche ${answers.web_search}, ohne Websuche ${answers.without_web_search}, Suchstatus unbekannt ${answers.search_unknown}).`}</li>
            <li>{`Eigenes Zitat nachgewiesen: ${answers.own_cited}; kein eigenes Zitat nachgewiesen: ${answers.own_not_cited}; Zitatstatus unbekannt: ${answers.own_unknown}.`}</li>
            <li>{`Altbestände ohne erfasste Suchausführung: ${answers.legacy}; Suchfallbacks: ${answers.search_fallback}.`}</li>
            <li>{`Fehlversuche ohne Antwort: ${answers.failed_attempts_without_answer}.`}</li>
            <li>{`Quellen: ${sources.total} (aufgelöst ${sources.resolved}, ausstehend ${sources.pending}, nicht auflösbar ${sources.unresolved}, blockiert ${sources.blocked}, ungültig ${sources.invalid}); ohne Zitatzuordnung: ${sources.citation_evidence_unknown}.`}</li>
            {quality.warnings.map((w) => <li key={w}>{WARNING_LABELS[w] ?? w}</li>)}
            <li>{`Stand: ${new Date(data.computed_at).toLocaleString('de-DE')}. Spätere Quellenauflösung kann Werte rückwirkend verändern.`}</li>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

/** Gate: rendert nur bei gebuchtem Modul; ohne Modul gibt es keinen /citations-Request. */
const CitationSharePanel: React.FC<PanelProps> = (props) => {
  const { active } = useCitationModuleActive(props.tenantId);
  if (!active) return null;
  return <CitationSharePanelContent {...props} />;
};

export default CitationSharePanel;
